'use client';

import { useEffect, useRef } from 'react';

const CHUNK_RELOAD_KEY = 'global-chunk-reload';

/** Chunk load failures (e.g. after deploy) need full reload to fetch new chunk URLs; reset() retries the same broken chunk. */
function isChunkLoadError(error: Error & { digest?: string }): boolean {
  const msg = typeof error?.message === 'string' ? error.message : '';
  return msg.includes('Loading chunk') || msg.includes('ChunkLoadError') || msg.includes('Failed to fetch dynamically imported module');
}

/** Next.js 15.5.x / Turbopack dev quirk – harmless, app works; treat as recoverable. */
function isCachedShadowRootError(error: Error & { digest?: string }): boolean {
  const msg = typeof error?.message === 'string' ? error.message : '';
  return msg.includes('cachedShadowRoot');
}

/** React hooks (e.g. useState) used outside component or duplicate React – suggest full reload / dedupe. */
function isUseStateContextError(error: Error & { digest?: string }): boolean {
  const msg = typeof error?.message === 'string' ? error.message : '';
  return msg.includes("'useState'") || msg.includes('reading \'useState\'');
}

// Global error boundary - catches errors in root layout
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const message = typeof error?.message === 'string' ? error.message : 'Something went wrong';
  const isDev = process.env.NODE_ENV === 'development';
  const isChunk = isChunkLoadError(error);
  const isShadowRoot = isCachedShadowRootError(error);
  const isUseState = isUseStateContextError(error);
  const didChunkReload = useRef(false);

  useEffect(() => {
    if (!isChunk || didChunkReload.current || typeof window === 'undefined') return;
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    didChunkReload.current = true;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
  }, [isChunk]);

  const handleRetry = () => {
    if (isChunk || isUseState) {
      window.location.reload();
      return;
    }
    reset();
  };

  if (isChunk && typeof window !== 'undefined' && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    return (
      <html>
        <body>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'system-ui' }}>
            <p style={{ color: '#64748b' }}>Loading latest version…</p>
          </div>
        </body>
      </html>
    );
  }

  if (isShadowRoot) {
    return (
      <html>
        <body>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            fontFamily: 'system-ui, sans-serif',
            textAlign: 'center',
            padding: '2rem'
          }}>
            <div style={{ maxWidth: '28rem' }}>
              <p style={{ fontSize: '1rem', color: '#666' }}>Dev-quirk ignorert (cachedShadowRoot). Alt fungerer.</p>
              <button onClick={reset} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>Fortsett</button>
            </div>
          </div>
        </body>
      </html>
    );
  }

  return (
    <html>
      <body>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{ maxWidth: '42rem' }}>
            <h1 style={{ fontSize: '4rem', margin: 0 }}>Error</h1>
            <p style={{ fontSize: '1.2rem', marginTop: '1rem' }}>
              {isChunk ? 'The page may be out of date. Reload to get the latest version.' : isUseState ? 'React context error (e.g. duplicate React or hooks outside component). Reload the page. If it persists, run: npm dedupe' : message}
            </p>
            {isDev && error?.stack && (
              <pre style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#f5f5f5',
                fontSize: '0.75rem',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: '12rem'
              }}>
                {error.stack}
              </pre>
            )}
            {isDev && (
              <p style={{ fontSize: '0.875rem', marginTop: '1rem', color: '#666' }}>
                Check server/PM2 logs for full stack (e.g. <code>pm2 logs luminawater-dev</code>).
              </p>
            )}
            <button
              onClick={handleRetry}
              style={{
                marginTop: '2rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#0070f3',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer'
              }}
            >
              {isChunk || isUseState ? 'Reload page' : 'Try Again'}
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
